import { useState } from 'react';

import cx from 'classnames';

import Modal from 'components/modal';
import { IS_INDIAN_EXCHANGE, isRNWebview } from 'constants/constants';
import { STORAGE_KEYS } from 'constants/enums';
import { MIXPANEL_EVENT_NAMES, MIXPANEL_SOURCES } from 'constants/mixpanelEventNames';
import { trackMixpanelEvent } from 'helpers/mixpanel-init';
import { generateExchangeURL, navigateTo } from 'helpers/url';
import useLocalStorage from 'hooks/useLocalStorage';
import useCustomMediaQuery from 'hooks/useMediaQuery';
import i18n from 'i18n/config';
import { useAppSelector } from 'storeHooks';
import { AngleLeft } from 'styles/assets/icons';
import DeltaButton from 'UIKit/DeltaButton';

import Content, { CardType } from './Content';
import Header from './Header';
import styles from './IndiaOrGlobalModal.module.scss';
import RememberMyChoice from './RememberMyChoice';

const IndiaOrGlobalModal = () => {
  const [state, setState] = useState({
    activeCard: CardType.GLOBAL,
    rememberMyChoice: false,
    modalOpen: true,
  });
  const { whenBelowMd } = useCustomMediaQuery();

  const country = useAppSelector(reduxState => reduxState?.other?.countryFromIp);

  const [exchangePreference, setExchangePreference] = useLocalStorage(
    STORAGE_KEYS.INDIA_OR_GLOBAL_MODAL_CHOICE,
    ''
  );

  const [sessionExchangePreference, setSessionExchangePreference] = useLocalStorage(
    STORAGE_KEYS.INDIA_OR_GLOBAL_MODAL_SESSION_CHOICE,
    {
      card: '',
      timestamp: 0,
    }
  );

  const buttonLabel =
    state.activeCard === CardType.INDIA
      ? i18n.t('common:stayOnDeltaIndia')
      : i18n.t('common:goToDeltaExchangeGlobal');

  const handleSubmit = () => {
    if (state.rememberMyChoice) {
      setExchangePreference(state.activeCard);
    } else {
      const preferenceData = {
        card: state.activeCard,
        timestamp: Date.now(),
      };
      setSessionExchangePreference(preferenceData);
    }

    trackMixpanelEvent(MIXPANEL_EVENT_NAMES.INDIA_OR_GLOBAL_MODAL.SUBMIT, {
      site_demarkation: MIXPANEL_SOURCES.WEB_APP,
      preference: state.activeCard === CardType.INDIA ? 'India' : 'Global',
    });
    if (state.activeCard === CardType.GLOBAL) {
      const exchangeUrl = generateExchangeURL(state.activeCard);
      navigateTo(exchangeUrl, '_self');
    }
    setState({ ...state, modalOpen: false });
  };

  const changeActiveCard = (cardType: CardType) => {
    setState({ ...state, activeCard: cardType });
    trackMixpanelEvent(MIXPANEL_EVENT_NAMES.INDIA_OR_GLOBAL_MODAL.CHANGE_PREFERENCE, {
      site_demarkation: MIXPANEL_SOURCES.WEB_APP,
      preference: cardType === CardType.INDIA ? 'India' : 'Global',
    });
  };

  const handleRememberMyChoice = () => {
    setState({ ...state, rememberMyChoice: !state.rememberMyChoice });
    trackMixpanelEvent(MIXPANEL_EVENT_NAMES.INDIA_OR_GLOBAL_MODAL.REMEMBER_MY_CHOICE, {
      site_demarkation: MIXPANEL_SOURCES.WEB_APP,
      preference: state.activeCard === CardType.INDIA ? 'India' : 'Global',
    });
  };
  const isCI = process.env.REACT_APP_ENV === 'ci';

  const showPreferenceModal = Boolean(
    IS_INDIAN_EXCHANGE &&
      !isRNWebview() &&
      country &&
      country !== 'India' &&
      !exchangePreference &&
      !isCI &&
      !sessionExchangePreference.timestamp
  );

  if (!showPreferenceModal) {
    return null;
  }

  return (
    <Modal
      isOpen={state.modalOpen}
      onRequestClose={() => {
        setState({ ...state, modalOpen: false });
      }}
      contentLabel="IndiaOrGlobalModal"
      className={whenBelowMd ? 'footer-common-modal' : 'common-modal-content'}
    >
      <div className={cx(styles.root, 'flex-set flex--column')}>
        <Header onClose={() => setState({ ...state, modalOpen: false })} />
        <Content activeCard={state.activeCard} changeActiveCard={changeActiveCard} />
        <div className={cx(styles.bottomContainer, 'flex-set flex--column')}>
          <RememberMyChoice
            checked={state.rememberMyChoice}
            handleCheck={handleRememberMyChoice}
            checkboxClassName={cx({
              [styles.indianCheckbox]: state.activeCard === CardType.INDIA,
              [styles.globalCheckbox]: state.activeCard === CardType.GLOBAL,
            })}
          />
          <DeltaButton
            data-testid="R6hEwKPokIbqFvRW_7F_1"
            className={cx('flex-set flex--content-center', styles.submitButton, {
              [styles.indianButton]: state.activeCard === CardType.INDIA,
              [styles.globalButton]: state.activeCard === CardType.GLOBAL,
            })}
            onClick={handleSubmit}
          >
            {buttonLabel} <AngleLeft />
          </DeltaButton>
        </div>
      </div>
    </Modal>
  );
};

export default IndiaOrGlobalModal;
